import React, { useState, useEffect } from 'react';
import { X, Clock, User, Calendar, DollarSign, Tag, CheckCircle, AlertCircle, Play, FileText, Plus, Copy, ExternalLink } from 'lucide-react';
import { api } from '../auth/authStore';
import toast from 'react-hot-toast';
import { RecurrenceBadge } from '../../components/ui/RecurrenceBadge';
import { FutureDueDates } from '../../components/ui/FutureDueDates';
import { ClientNotes } from '../clients/ClientNotes';

interface TaskDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onEdit: () => void;
  taskId: string | null;
}

interface TaskDetails {
  id: string;
  type: string;
  title: string;
  description?: string;
  clientId: string;
  clientName?: string;
  status: string;
  documentsRepositoryUrl?: string;
  assignees: {
    preparerId?: string;
    reviewerId?: string;
    submitterId?: string;
  };
  assigneeNames?: {
    preparer?: string;
    reviewer?: string;
    submitter?: string;
  };
  schedule: {
    frequency: string;
    dueDate: string;
    periodStart?: string;
    periodEnd?: string;
  };
  checklist: Array<{
    _id: string;
    label: string;
    done: boolean;
    required: boolean;
    order: number;
    doneAt?: string;
    doneBy?: string;
  }>;
  timeSpentMinutes: number;
  billing: {
    feeType: string;
    fixedAmount?: number;
    billableRate?: number;
    estimatedHours?: number;
  };
  priority: 'low' | 'medium' | 'high' | 'urgent';
  tags: string[];
  completionPercentage: number;
  activityLog: Array<{
    at: string;
    by: string;
    action: string;
    description?: string;
    meta?: any;
  }>;
  createdAt: string;
  updatedAt: string;
  recurrenceInfo?: {
    isRecurring: boolean;
    description: string;
    nextDueDates: string[];
  };
}

// Documents Repository Display Component
interface DocumentsRepositoryDisplayProps {
  url: string;
}

const DocumentsRepositoryDisplay: React.FC<DocumentsRepositoryDisplayProps> = ({ url }) => {
  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success('URL copied to clipboard');
    } catch (err) {
      toast.error('Failed to copy URL');
    }
  };

  const openUrl = () => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FileText className="w-4 h-4 text-blue-600" />
          <span className="text-sm font-medium text-blue-900">Documents Repository</span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={copyToClipboard}
            className="p-1 text-blue-600 hover:text-blue-800"
            title="Copy URL"
          >
            <Copy className="w-4 h-4" />
          </button>
          <button
            onClick={openUrl}
            className="p-1 text-blue-600 hover:text-blue-800"
            title="Open URL"
          >
            <ExternalLink className="w-4 h-4" />
          </button>
        </div>
      </div>
      <p className="text-sm text-blue-700 mt-1 break-all">{url}</p>
    </div>
  );
};

export const TaskDetailsModal: React.FC<TaskDetailsModalProps> = ({
  isOpen,
  onClose,
  onEdit,
  taskId
}) => {
  const [loading, setLoading] = useState(false);
  const [task, setTask] = useState<TaskDetails | null>(null);
  const [timeEntry, setTimeEntry] = useState({ minutes: '', description: '' });
  const [addingTime, setAddingTime] = useState(false);

  useEffect(() => {
    if (isOpen && taskId) {
      fetchTaskDetails();
    }
  }, [isOpen, taskId]);

  const fetchTaskDetails = async () => {
    if (!taskId) return;

    setLoading(true);
    try {
      const response = await api.get(`/firm-admin/tasks/${taskId}`);
      if (response.data.success) {
        setTask(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching task details:', error);
      toast.error('Failed to load task details');
    } finally {
      setLoading(false);
    }
  };

  const handleChecklistUpdate = async (itemId: string, done: boolean) => {
    if (!task) return;

    try {
      const response = await api.patch(`/firm-admin/tasks/${task.id}/checklist/${itemId}`, {
        done
      });
      
      if (response.data.success) {
        // Update local state
        setTask(prev => {
          if (!prev) return prev;
          return {
            ...prev,
            checklist: prev.checklist.map(item =>
              item._id === itemId ? { ...item, done } : item
            )
          };
        });
        toast.success('Checklist item updated');
      }
    } catch (error: any) {
      console.error('Error updating checklist item:', error);
      toast.error('Failed to update checklist item');
    }
  };

  const handleAddTime = async () => {
    if (!task || !timeEntry.minutes) return;

    setAddingTime(true);
    try {
      const response = await api.post(`/firm-admin/tasks/${task.id}/time`, {
        minutes: parseInt(timeEntry.minutes),
        description: timeEntry.description
      });
      
      if (response.data.success) {
        setTask(prev => {
          if (!prev) return prev;
          return {
            ...prev,
            timeSpentMinutes: prev.timeSpentMinutes + parseInt(timeEntry.minutes)
          };
        });
        setTimeEntry({ minutes: '', description: '' });
        toast.success('Time added successfully');
        fetchTaskDetails(); // Refresh to get updated activity log
      }
    } catch (error: any) {
      console.error('Error adding time:', error);
      toast.error('Failed to add time');
    } finally {
      setAddingTime(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'COMPLETED':
      case 'SUBMITTED':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'IN_PROGRESS':
        return <Play className="w-5 h-5 text-indigo-500" />;
      case 'REVIEW':
        return <AlertCircle className="w-5 h-5 text-yellow-500" />;
      case 'TODO':
        return <Clock className="w-5 h-5 text-blue-500" />;
      case 'DRAFT':
      default:
        return <FileText className="w-5 h-5 text-gray-500" />;
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent':
        return 'bg-red-100 text-red-800';
      case 'high':
        return 'bg-orange-100 text-orange-800';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800';
      case 'low':
      default:
        return 'bg-green-100 text-green-800';
    }
  };

  const formatTime = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            Task Details
          </h2>
          <div className="flex items-center gap-2">
            <button
              onClick={onEdit}
              className="px-3 py-1 text-sm bg-primary-600 text-white rounded hover:bg-primary-700"
            >
              Edit
            </button>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center p-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          </div>
        ) : task ? (
          <div className="p-6 space-y-6">
            {/* Task Header */}
            <div>
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{task.title}</h3>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      {getStatusIcon(task.status)}
                      <span className="text-sm font-medium">
                        {task.status.replace('_', ' ')}
                      </span>
                    </div>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getPriorityColor(task.priority)}`}>
                      {task.priority.charAt(0).toUpperCase() + task.priority.slice(1)} Priority
                    </span>
                    <span className="text-sm text-gray-500">
                      {task.type.replace('_', ' ')}
                    </span>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-sm text-gray-500">Due Date</div>
                  <div className="font-medium">
                    {new Date(task.schedule.dueDate).toLocaleDateString()}
                  </div>
                </div>
              </div>

              {task.description && (
                <p className="text-gray-700 mb-4">{task.description}</p>
              )}

              {/* Recurrence Information */}
              {task.recurrenceInfo && (
                <div className="mb-4 space-y-3">
                  <div className="flex items-center space-x-3">
                    <RecurrenceBadge
                      recurrenceInfo={task.recurrenceInfo}
                      size="md"
                      showDescription={true}
                    />
                  </div>

                  {task.recurrenceInfo.isRecurring && task.recurrenceInfo.nextDueDates.length > 0 && (
                    <FutureDueDates
                      nextDueDates={task.recurrenceInfo.nextDueDates}
                      maxDates={5}
                    />
                  )}
                </div>
              )}

              {/* Documents Repository URL */}
              {task.documentsRepositoryUrl && (
                <DocumentsRepositoryDisplay url={task.documentsRepositoryUrl} />
              )}

              {task.tags.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {task.tags.map((tag, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full"
                    >
                      <Tag className="w-3 h-3 mr-1" />
                      {tag}
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Task Info Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Client Info */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-medium text-gray-900 mb-2">Client</h4>
                <p className="text-gray-700">{task.clientName || 'Unknown Client'}</p>
              </div>

              {/* Time Tracking */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-medium text-gray-900 mb-2">Time Tracking</h4>
                <div className="space-y-1">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Spent:</span>
                    <span className="font-medium">{formatTime(task.timeSpentMinutes)}</span>
                  </div>
                  {task.billing.estimatedHours && (
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Estimated:</span>
                      <span className="font-medium">{task.billing.estimatedHours}h</span>
                    </div>
                  )}
                  <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                    <div 
                      className="bg-primary-600 h-2 rounded-full" 
                      style={{ 
                        width: `${Math.min(task.completionPercentage, 100)}%` 
                      }}
                    ></div>
                  </div>
                  <div className="text-xs text-gray-500 text-center">
                    {task.completionPercentage}% Complete
                  </div>
                </div>
              </div>

              {/* Billing Info */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-medium text-gray-900 mb-2">Billing</h4>
                <div className="space-y-1">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Type:</span>
                    <span className="font-medium">{task.billing.feeType}</span>
                  </div>
                  {task.billing.fixedAmount && (
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Fixed:</span>
                      <span className="font-medium">${task.billing.fixedAmount}</span>
                    </div>
                  )}
                  {task.billing.billableRate && (
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Rate:</span>
                      <span className="font-medium">${task.billing.billableRate}/hr</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Assignees */}
            <div>
              <h4 className="font-medium text-gray-900 mb-3">Assignees</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <User className="w-5 h-5 text-gray-400" />
                  <div>
                    <div className="text-sm font-medium">Preparer</div>
                    <div className="text-sm text-gray-600">
                      {task.assigneeNames?.preparer || 'Unassigned'}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <User className="w-5 h-5 text-gray-400" />
                  <div>
                    <div className="text-sm font-medium">Reviewer</div>
                    <div className="text-sm text-gray-600">
                      {task.assigneeNames?.reviewer || 'Unassigned'}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <User className="w-5 h-5 text-gray-400" />
                  <div>
                    <div className="text-sm font-medium">Submitter</div>
                    <div className="text-sm text-gray-600">
                      {task.assigneeNames?.submitter || 'Unassigned'}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Checklist */}
            {task.checklist.length > 0 && (
              <div>
                <h4 className="font-medium text-gray-900 mb-3">Checklist</h4>
                <div className="space-y-2">
                  {task.checklist.map((item) => (
                    <div key={item._id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                      <input
                        type="checkbox"
                        checked={item.done}
                        onChange={(e) => handleChecklistUpdate(item._id, e.target.checked)}
                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      <span className={`flex-1 ${item.done ? 'line-through text-gray-500' : 'text-gray-900'}`}>
                        {item.label}
                      </span>
                      {item.required && (
                        <span className="text-xs text-red-600">Required</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Add Time Entry */}
            <div>
              <h4 className="font-medium text-gray-900 mb-3">Add Time</h4>
              <div className="flex gap-4">
                <input
                  type="number"
                  placeholder="Minutes"
                  value={timeEntry.minutes}
                  onChange={(e) => setTimeEntry(prev => ({ ...prev, minutes: e.target.value }))}
                  className="w-32 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
                <input
                  type="text"
                  placeholder="Description (optional)"
                  value={timeEntry.description}
                  onChange={(e) => setTimeEntry(prev => ({ ...prev, description: e.target.value }))}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
                <button
                  onClick={handleAddTime}
                  disabled={!timeEntry.minutes || addingTime}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {addingTime ? 'Adding...' : 'Add Time'}
                </button>
              </div>
            </div>

            {/* Activity Log */}
            {task.activityLog && task.activityLog.length > 0 && (
              <div>
                <h4 className="font-medium text-gray-900 mb-3">Activity Log</h4>
                <div className="space-y-3 max-h-64 overflow-y-auto">
                  {task.activityLog.map((activity, index) => (
                    <div key={index} className="flex gap-3 p-3 bg-gray-50 rounded-lg">
                      <div className="w-2 h-2 bg-primary-600 rounded-full mt-2"></div>
                      <div className="flex-1">
                        <div className="text-sm text-gray-900">
                          {activity.description || activity.action}
                        </div>
                        <div className="text-xs text-gray-500">
                          {activity.by} • {new Date(activity.at).toLocaleString()}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Client Notes */}
            {task.clientId && (
              <div>
                <ClientNotes
                  clientId={task.clientId}
                  clientName={task.clientName}
                  showHeader={true}
                  maxHeight="300px"
                />
              </div>
            )}
          </div>
        ) : (
          <div className="p-12 text-center text-gray-500">
            Task not found
          </div>
        )}
      </div>
    </div>
  );
};
